import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { i18nLanguage } from "i18n";
import { TripperData, TripperDisplayMode, SegmentData, StopBlock, GroupBlock, OperatorRecord } from "../types";
import { DistanceUnit } from "shared/types";
import { formatDistance } from "shared/utils/formatDistance";
import { resolveDistanceUnit } from "shared/utils/resolveDistanceUnit";

import { TripperMap } from "./TripperMap";
import { TripperContent } from "./TripperContent";
import { useScrollSync } from "../hooks/useScrollSync";
import { useBusinessTypeFilterSync } from "../hooks/useBusinessTypeFilterSync";
import { useTripperTracking } from "../hooks/useTripperTracking";
import { useTripperScrollContext, useTripperScrollProgressContext, useAtlasMode, useForceDesktop, useVerticalStack } from "../context/TripperWidgetContext";
import { TripperProgressPill } from "./content/TripperProgressPill";
import { StopInfoPanel, StopInfoPanelData } from "./content/StopInfoPanel";
import { TripperBusinessTypeFilters } from "./content/TripperBusinessTypeFilters";
import { MobileMapCarousel } from "./mobile/MobileMapCarousel";

type TripperLayoutProps = {
  data: TripperData;
  language: i18nLanguage;
  displayMode: TripperDisplayMode;
  onStopDetailsOpenChange?: (isOpen: boolean) => void;
};

const MapToggleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
    <line x1="9" y1="3" x2="9" y2="18" />
    <line x1="15" y1="6" x2="15" y2="21" />
  </svg>
);

const ListToggleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const toRad = (deg: number) => (deg * Math.PI) / 180;

const haversineMeters = (a: [number, number], b: [number, number]): number => {
  const R = 6371000;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);

  const h =
    Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const getStopCoordinates = (
  stop: StopBlock,
  operatorByEntityId: Record<string, OperatorRecord>,
): [number, number] | null => {
  const entityId = stop.entityRef?.entityId?.toString();
  if (!entityId) return null;
  const coordinates = operatorByEntityId[entityId]?.contact?.address?.location?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length !== 2) return null;
  const [lng, lat] = coordinates;
  if (typeof lng !== "number" || typeof lat !== "number") return null;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return [lng, lat];
};

function getSegmentDistanceMeters(
  segment: SegmentData,
  operatorByEntityId: Record<string, OperatorRecord>,
): number {
  let meters = 0;
  let previousStop: StopBlock | null = null;
  for (const block of segment.items) {
    if (block.blockType === "STOP") {
      const stop = block as StopBlock;
      const routeMeters = stop.distanceFromPrev ?? 0;
      if (routeMeters > 0) {
        meters += routeMeters;
      } else if (previousStop) {
        const from = getStopCoordinates(previousStop, operatorByEntityId);
        const to = getStopCoordinates(stop, operatorByEntityId);
        if (from && to) meters += haversineMeters(from, to);
      }
      previousStop = stop;
    } else if (block.blockType === "GROUP") {
      for (const child of (block as GroupBlock).items) {
        if (child.blockType === "STOP") {
          const stop = child as StopBlock;
          const routeMeters = stop.distanceFromPrev ?? 0;
          if (routeMeters > 0) {
            meters += routeMeters;
          } else if (previousStop) {
            const from = getStopCoordinates(previousStop, operatorByEntityId);
            const to = getStopCoordinates(stop, operatorByEntityId);
            if (from && to) meters += haversineMeters(from, to);
          }
          previousStop = stop;
        }
      }
    }
  }
  return meters;
}

export const TripperLayout = ({
  data,
  language,
  displayMode,
  onStopDetailsOpenChange,
}: TripperLayoutProps) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const { activeItemId, activeSegmentId, isMetadataInView } =
    useTripperScrollContext();
  const { segmentScrollProgress } = useTripperScrollProgressContext();
  const atlasMode = useAtlasMode();
  const forceDesktop = useForceDesktop();
  const verticalStack = useVerticalStack();
  const useFixedLayout = forceDesktop || verticalStack;
  const [mobileView, setMobileView] = useState<"list" | "map">("list");
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [infoPanelData, setInfoPanelData] = useState<StopInfoPanelData | null>(null);

  useEffect(() => {
    onStopDetailsOpenChange?.(infoPanelData !== null);
    return () => onStopDetailsOpenChange?.(false);
  }, [infoPanelData, onStopDetailsOpenChange]);

  const { trackOperatorView, trackOutboundClick } = useTripperTracking(data);

  // --- View-switch scroll restoration ---
  // Keep a ref to activeItemId so the layout effect can read it without
  // adding it to the dependency list (we only want to fire on view change).
  const activeItemIdRef = useRef<string | null>(null);
  activeItemIdRef.current = activeItemId;
  const prevMobileViewRef = useRef(mobileView);

  // When switching from carousel back to list, scroll the content panel
  // to the stop that was active in the carousel — before useScrollSync
  // re-calculates (layout effects fire before regular effects).
  useLayoutEffect(() => {
    const wasMap = prevMobileViewRef.current === "map";
    prevMobileViewRef.current = mobileView;

    if (!wasMap || mobileView !== "list") return;

    const itemId = activeItemIdRef.current;
    if (!itemId) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const target = container.querySelector<HTMLElement>(`[data-item-id="${itemId}"]`);
    target?.scrollIntoView({ behavior: "instant", block: "start" });
  }, [mobileView]);

  // Detect mobile layout via ResizeObserver so we can skip mounting the map
  // in list view (avoids Mapbox tile loads the user never sees).
  useEffect(() => {
    if (useFixedLayout) {
      setIsMobileLayout(false);
      return;
    }

    const el = layoutRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setIsMobileLayout(entry.contentRect.width < 768);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [useFixedLayout]);

  useEffect(() => {
    if (useFixedLayout && mobileView !== "list") {
      setMobileView("list");
    }
  }, [useFixedLayout, mobileView]);

  // Prefer the visitor's own browser region (a US traveler should see miles no
  // matter who built the itinerary); fall back to the organization's country.
  const initialUnit = useMemo<DistanceUnit>(
    () =>
      resolveDistanceUnit(
        data.itinerary?.organization?.contact?.address?.country,
      ),
    [data.itinerary?.organization?.contact?.address?.country],
  );
  const [distanceUnit, setDistanceUnit] = useState<DistanceUnit>(initialUnit);
  const toggleUnit = useCallback(() => {
    setDistanceUnit(u => u === "metric" ? "imperial" : "metric");
  }, []);

  const handleStopNameClick = useCallback(
    (stop: StopBlock, operator: OperatorRecord, markerNumber: number) => {
      setInfoPanelData({ stop, operator, markerNumber });
      if (operator?._entityId) {
        trackOperatorView(operator._entityId);
      }
    },
    [trackOperatorView],
  );

  const { scrollToItem } = useScrollSync({
    scrollContainerRef,
    resetKey: mobileView,
  });

  const handleMarkerClick = useCallback(
    (itemId: string) => {
      if (infoPanelData !== null && infoPanelData.stop._id !== itemId) {
        setInfoPanelData(null);
      }
      scrollToItem(itemId);
    },
    [infoPanelData, scrollToItem],
  );

  useBusinessTypeFilterSync({ data, scrollContainerRef });

  const visibleSegments = useMemo(
    () => data.segments.filter(s => s.items.length > 0),
    [data.segments],
  );

  const activeSegment = useMemo(
    () => visibleSegments.find(s => s._id === activeSegmentId) ?? visibleSegments[0],
    [activeSegmentId, visibleSegments],
  );

  const activeSegmentDistance = useMemo(() => {
    if (!activeSegment) return null;
    const meters = getSegmentDistanceMeters(activeSegment, data.operatorByEntityId);
    return meters > 0 ? formatDistance(meters, distanceUnit) : null;
  }, [activeSegment, distanceUnit, data.operatorByEntityId]);

  const activeSegmentIndex = useMemo(
    () =>
      activeSegment
        ? visibleSegments.findIndex(s => s._id === activeSegment._id)
        : -1,
    [activeSegment, visibleSegments],
  );

  const segmentProgress = activeSegment
    ? segmentScrollProgress[activeSegment._id] ?? 0
    : 0;

  const isDayTrip = data.itinerary?.tripperSettings?.segmentPreset === "DAY_TRIP";

  const totalDistance = useMemo(() => {
    if (!isDayTrip) return null;
    const meters = visibleSegments.reduce(
      (sum, seg) => sum + getSegmentDistanceMeters(seg, data.operatorByEntityId),
      0,
    );
    return meters > 0 ? formatDistance(meters, distanceUnit) : null;
  }, [isDayTrip, visibleSegments, distanceUnit, data.operatorByEntityId]);

  const overallProgress = useMemo(() => {
    if (!isDayTrip || visibleSegments.length === 0) return 0;
    const currentSegmentProgress = activeSegment
      ? segmentScrollProgress[activeSegment._id] ?? 0
      : 0;
    return (activeSegmentIndex + currentSegmentProgress) / visibleSegments.length;
  }, [isDayTrip, visibleSegments, activeSegmentIndex, activeSegment, segmentScrollProgress]);

  if (displayMode === "map-only") {
    return (
      <div style={{ width: "100%", height: "100svh" }}>
        <TripperMap data={data} onMarkerClick={scrollToItem} />
      </div>
    );
  }

  if (displayMode === "print") {
    return (
      <div className="tripper-layout tripper-layout--print" data-tripper-print-ready>
        <div className="tripper-layout__print-content">
          <TripperContent forPdf data={data} language={language} unit={distanceUnit} />
        </div>
      </div>
    );
  }

  const fabButton = (
    <button
      type="button"
      className="tripper-mobile-toggle"
      onClick={() => setMobileView(v => (v === "list" ? "map" : "list"))}
      aria-label={mobileView === "list" ? "Show map" : "Show list"}
    >
      <span
        className={`tripper-mobile-toggle__inner${mobileView === "map" ? " tripper-mobile-toggle__inner--flipped" : ""}`}
      >
        <span className="tripper-mobile-toggle__face">
          <MapToggleIcon />
        </span>
        <span className="tripper-mobile-toggle__face tripper-mobile-toggle__face--back">
          <ListToggleIcon />
        </span>
      </span>
    </button>
  );

  // Mobile map view: full-screen map with bottom carousel
  if (!useFixedLayout && mobileView === "map") {
    return (
      <div className="tripper-layout tripper-layout--carousel">
        <MobileMapCarousel
          data={data}
          language={language}
          unit={distanceUnit}
          onStopClick={handleStopNameClick}
          onMarkerClick={handleMarkerClick}
        />
        <StopInfoPanel
          data={infoPanelData}
          language={language}
          onClose={() => setInfoPanelData(null)}
          onOutboundClick={trackOutboundClick}
        />
        {fabButton}
      </div>
    );
  }

  return (
    <div className="tripper-layout-shell">
      <div ref={layoutRef} className="tripper-layout" data-mobile-view={mobileView}>
      {/* Map panel — left (unmounted on mobile list view to avoid Mapbox tile costs) */}
      {!(isMobileLayout && mobileView === "list") && (
        <div className="tripper-layout__map tripper-map-panel">
          <TripperMap data={data} onMarkerClick={handleMarkerClick} />
          <TripperBusinessTypeFilters data={data} />
        </div>
      )}

      {/* Content panel — right */}
      <div className="tripper-layout__content-wrapper">
        {activeSegment && !atlasMode && (
          <div
            className="tripper-progress-overlay"
            style={{
              opacity: isMetadataInView ? 0 : 1,
              pointerEvents: isMetadataInView ? "none" : "auto",
              transform: isMetadataInView ? "translateY(8px)" : "translateY(0)",
              transition: "opacity 240ms ease, transform 240ms ease",
            }}
          >
            <div className="tripper-progress-overlay__top-fade" aria-hidden />
            <div className="tripper-progress-row">
              <TripperProgressPill
                dayNumber={Math.max(1, activeSegmentIndex + 1)}
                distance={isDayTrip
                  ? (totalDistance ?? "")
                  : (activeSegmentDistance ?? "")
                }
                progress={isDayTrip ? overallProgress : segmentProgress}
                isComplete={isDayTrip
                  ? overallProgress >= 0.998
                  : (activeSegmentIndex === visibleSegments.length - 1 && segmentProgress >= 0.998)
                }
                label={isDayTrip ? "" : undefined}
                onToggleUnit={toggleUnit}
              />
            </div>
          </div>
        )}

        <div
          ref={scrollContainerRef}
          className="tripper-content-scroll tripper-layout__content"
        >
          <TripperContent data={data} language={language} onStopNameClick={handleStopNameClick} onOutboundClick={trackOutboundClick} unit={distanceUnit} />
        </div>

        <StopInfoPanel
          data={infoPanelData}
          language={language}
          onClose={() => setInfoPanelData(null)}
          onOutboundClick={trackOutboundClick}
        />
      </div>

      {!useFixedLayout && fabButton}
      </div>
    </div>
  );
};
